import React, { useState } from 'react';
import { Box, TextField, Button, IconButton, Typography, Paper, Autocomplete } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Field } from './edit-object/design/Field';


export interface Edition {
  type: string;
  count: number;
  total: number;
}

interface EditionCounterProps {
  label: string;
  addButtonText: string;
  options: readonly string[];
  value?: Edition[];
  onChange?: (editions: Edition[]) => void;
  freeTextSentinel?: string;
  /**
   * 'mui' (default) renders the legacy MUI Autocomplete + TextField
   * label. 'design' wraps the Autocomplete in the shared Field
   * primitive so the label is always visible and uppercase, in line
   * with the rest of the edit-object hi-fi panel.
   */
  variant?: 'mui' | 'design';
}

export const EditionCounter = ({ label, addButtonText, options, value = [], onChange, freeTextSentinel, variant = 'mui' }: EditionCounterProps) => {
  const theme = useTheme();
  const [selectedType, setSelectedType] = useState('');
  const [isFreeTextMode, setIsFreeTextMode] = useState(false);
  const [freeTextValue, setFreeTextValue] = useState('');
  const editions = value;

  const isSentinel = (type: string) =>
    freeTextSentinel !== undefined && type === freeTextSentinel;

  const addEdition = () => {
    const typeToAdd = isFreeTextMode ? freeTextValue.trim() : selectedType;
    if (typeToAdd && !editions.find(e => e.type === typeToAdd)) {
      const newEditions = [...editions, { type: typeToAdd, count: 1, total: 1 }];
      onChange?.(newEditions);
      setSelectedType('');
      setIsFreeTextMode(false);
      setFreeTextValue('');
    }
  };

  const updateCount = (index: number, newCount: number) => {
    if (newCount >= 0) {
      const newEditions = editions.map((e, i) =>
        i === index ? { ...e, count: newCount, total: newCount } : e
      );
      onChange?.(newEditions);
    }
  };

  const removeEdition = (index: number) => {
    const newEditions = editions.filter((_, i) => i !== index);
    onChange?.(newEditions);
  };

  const isAddDisabled = isFreeTextMode
    ? freeTextValue.trim() === '' || editions.some(e => e.type === freeTextValue.trim())
    : !selectedType || editions.some(e => e.type === selectedType);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {!isFreeTextMode && (
          <Autocomplete
            freeSolo
            fullWidth
            options={[...options]}
            value={selectedType}
            inputValue={selectedType}
            onInputChange={(_event, newInputValue) => {
              if (isSentinel(newInputValue)) {
                setIsFreeTextMode(true);
                setSelectedType('');
              } else {
                setSelectedType(newInputValue);
              }
            }}
            onChange={(_event, newValue) => {
              const raw = typeof newValue === 'string' ? newValue : '';
              if (isSentinel(raw)) {
                setIsFreeTextMode(true);
                setSelectedType('');
              } else {
                setSelectedType(raw);
              }
            }}
            sx={{ flex: 1 }}
            renderInput={(params) =>
              variant === 'design' ? (
                <Field label={label}>
                  <TextField
                    {...params}
                    variant="standard"
                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                    inputProps={{ ...params.inputProps, 'aria-label': label }}
                  />
                </Field>
              ) : (
                <TextField
                  {...params}
                  label={label}
                  variant="outlined"
                />
              )
            }
          />
        )}
        {isFreeTextMode && (
          <TextField
            fullWidth
            label="Custom Edition Text"
            inputProps={{ 'aria-label': 'Custom Edition Text' }}
            variant="outlined"
            value={freeTextValue}
            onChange={(e) => setFreeTextValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsFreeTextMode(false);
                setFreeTextValue('');
              }
            }}
            sx={{ flex: 1 }}
          />
        )}
        <Button
          variant="outlined"
          onClick={addEdition}
          disabled={isAddDisabled}
          startIcon={<Add />}
        >
          {addButtonText}
        </Button>
      </Box>

      {editions.map((edition, index) => (
        <Paper
          key={edition.type}
          sx={{
            p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Typography
            variant="body1"
            sx={{
              flexGrow: 1,
              color: theme.palette.text.primary
            }}
          >
            {edition.type}
          </Typography>

          <TextField
            size="small"
            type="number"
            value={edition.count}
            onChange={(e) => updateCount(index, parseInt(e.target.value) || 0)}
            sx={{ width: 80 }}
            slotProps={{ htmlInput: { min: 0 } }}
          />

          <IconButton
            size="small"
            color="error"
            onClick={() => removeEdition(index)}
          >
            <Delete />
          </IconButton>
        </Paper>
      ))}
    </Box>
  );
};
